import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, message, Popconfirm, Row, Space, Table } from "antd";
import { renderAddress } from "common/utils";
import { PageHeader } from "components/pageHeader";
import AddProcessServerModal from "features/processServersTable/components/addProcessServerModal";
import {
  useDeleteProcessServerMutation,
  useFetchProcessServersQuery,
} from "features/processServersTable/processServersAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useRef, useState } from "react";
import styled from "styled-components";
import { getColumnSearchProps } from "common/antd-utils";

const StyledTable = styled(Table)`
  cursor: pointer;
  margin-top: 12px;
`;

export default function ProcessServersTable() {
  const { data: constants } = useFetchBackendConstantsQuery();
  const [modalVisible, setModalVisible] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const { data: processServers, isLoading } = useFetchProcessServersQuery();
  const [deleteProcessServer] = useDeleteProcessServerMutation();

  const handleDelete = async (record) => {
    const result = await deleteProcessServer({
      processServerId: record.id,
    });

    if ("data" in result) {
      message.success("Court of law deleted successfully");
    }
  };

  const onCancel = async () => {
    setModalVisible(false);
    setActiveRecord(null);
  };

  const [filteredInfo, setFilteredInfo] = useState({});
  const searchInput = useRef(null);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };
  const handleReset = (clearFilters) => {
    clearFilters();
  };
  const handleChange = (_, filters) => {
    setFilteredInfo(filters);
  };

  const columns = [
    {
      title: "Process Server Code",
      dataIndex: "code",
    },
    {
      title: "Process Server Name",
      dataIndex: "name",
      render: (_, record) => record.name,
      ...getColumnSearchProps("name", searchInput, handleSearch, handleReset),
      // @ts-ignore
      // filteredValue: filteredInfo?.name ?? null,
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Department",
      dataIndex: "department",
      render: (text) => text || "-",
    },
    {
      title: "Address",
      render: (text, record) => renderAddress(record, constants?.states),
    },
    {
      title: "EIN",
      dataIndex: "ein",
      render: (text) => text || "-",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      render: (text) => text || "-",
    },
    {
      title: "FAX",
      dataIndex: "fax",
      render: (text) => text || "-",
    },
    {
      title: "Contact Name",
      dataIndex: "contactName",
      render: (text) => text || "-",
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (text) => text || "-",
    },
    {
      title: "Notes",
      dataIndex: "notes",
      render: (text) => text || "-",
    },
    {
      title: "Actions",
      fixed: "right",
      render: (text, record) => (
        <Space size="middle">
          <EditOutlined
            key="edit"
            onClick={() => {
              setActiveRecord(record);
              setModalVisible(true);
            }}
          />
          <Popconfirm
            placement="topLeft"
            okText="Yes"
            onConfirm={() => handleDelete(record)}
            title="Are you sure you want to delete this?"
          >
            <DeleteOutlined key="delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Col>
      <PageHeader>Process Servers</PageHeader>
      <Row align="middle">
        <span>Configure process servers and their locations.</span>
        <Button onClick={() => setModalVisible(true)} type="link" icon={<PlusOutlined />}>
          Add Process Server
        </Button>
      </Row>
      <StyledTable
        loading={isLoading}
        bordered
        scroll={{ x: "max-content" }}
        columns={columns}
        dataSource={processServers}
        onChange={handleChange}
      />
      {modalVisible && (
        <AddProcessServerModal record={activeRecord} onOk={onCancel} onCancel={onCancel} />
      )}
    </Col>
  );
}
